import { defer, isFunction } from 'lodash';
import { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';

import { AutoResizeTextArea } from '@/components/form/text-area/auto-resize-text-area';
import { Button, SplitButton } from '@/components/button';
import { DropdownMenuItemActionable } from '@/components/dropdown';
import { Icon } from '@/components/icon';
import { Dropdown } from '@/components/dropdown';
import { ChatMode } from '@/explore/types';

import { ChatSettings } from './chat-settings';
import { useChatSettings } from '../use-chat-settings';

import styles from '../chat-cell.module.scss';

type Hint = { label: string; hidden?: boolean };

interface UserMessageInputProps {
  initialValue?: string;
  sources?: string[];
  mode?: ChatMode;
  placeholder?: string;
  autoFocus?: boolean;
  primaryActions?:
    | DropdownMenuItemActionable[]
    | ((value: string, sources: string[], mode: ChatMode) => DropdownMenuItemActionable[]);
  secondaryActions?:
    | DropdownMenuItemActionable[]
    | ((value: string) => DropdownMenuItemActionable[]);
  hints?: Hint[] | ((value: string) => Hint[]);
  hideSettings?: boolean;
  className?: classNames.Argument;
  title?: string;
  disabled?: boolean;
  onSubmit: (value: string, sources: string[], mode: ChatMode) => void;
  onCancel?: () => void;
  onAccept?: () => void;
}

export const UserMessageInput = (props: UserMessageInputProps) => {
  const {
    initialValue = '',
    sources,
    mode,
    placeholder,
    autoFocus,
    primaryActions = [],
    secondaryActions = [],
    hints = [],
    hideSettings = false,
    disabled = false,
    className,
    title,
    onSubmit,
    onCancel,
    onAccept,
  } = props;

  const [value, setValue] = useState(initialValue);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const { selectedSources, selectedMode, setSettings } = useChatSettings({ sources, mode });

  useEffect(() => {
    setValue(initialValue.trim());
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus === true && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      setSettings({ sources: selectedSources, mode: selectedMode });
      if (value.length) {
        onSubmit(value, selectedSources, selectedMode);
      } else if (primaries.length > 1) {
        onAccept?.();
      }
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel?.();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (value.length === 0) {
      return;
    }

    setSettings({ sources: selectedSources, mode: selectedMode });
    onSubmit(value, selectedSources, selectedMode);
  };

  const handleSettingsClose = () => {
    setIsSettingsOpen(false);
    defer(() => inputRef.current?.focus());
  };

  const primaries = isFunction(primaryActions)
    ? primaryActions(value, selectedSources, selectedMode)
    : primaryActions;
  const secondaries = isFunction(secondaryActions) ? secondaryActions(value) : secondaryActions;

  return (
    <form className={classNames(className)} onSubmit={handleSubmit}>
      {title !== undefined && <div className={styles.title}>{title}</div>}

      {disabled ? (
        <p className={styles.disabledMessage}>This conversation cannot be continued</p>
      ) : (
        <AutoResizeTextArea
          ref={inputRef}
          value={value}
          onChange={(event) => setValue(event.currentTarget.value ?? '')}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'Legacy conversations cannot be continued' : placeholder}
          disabled={disabled}
          rows={1}
          onFocus={(e) => e.currentTarget.select()}
        />
      )}
      <div className={classNames([styles.inputActions])}>
        {primaries.length > 1 ? (
          <SplitButton
            size="small"
            variant="primary"
            actions={primaries}
            onClick={primaries[0]?.onClick}
          />
        ) : disabled ? (
          <></>
        ) : (
          <Button type="submit" variant="primary" size="compact" disabled={primaries[0].disabled}>
            {primaries[0].label}
          </Button>
        )}

        {secondaries.length > 1 ? (
          <Dropdown
            align="left"
            items={secondaries}
            trigger={(isOpen, setIsOpen) => (
              <Button
                size="small"
                type="button"
                variant="secondary"
                className={styles.secondaryAction}
                onClick={() => setIsOpen(!isOpen)}>
                Discard
                <Icon name="ChevronDown" />
              </Button>
            )}
          />
        ) : secondaries.length === 1 ? (
          <button
            type="button"
            className={classNames(styles.hint)}
            onClick={(event) => {
              event.preventDefault();
              secondaries[0]?.onClick?.(event);
            }}>
            {secondaries[0].label}
          </button>
        ) : null}

        {!hideSettings && !disabled && (
          <ChatSettings
            selectedSources={selectedSources}
            selectedMode={selectedMode}
            className={styles.settings}
            placement="bottom-end"
            onChange={setSettings}
            onClose={handleSettingsClose}
            isOpen={isSettingsOpen}
            onOpenChange={setIsSettingsOpen}
          />
        )}

        {(isFunction(hints) ? hints(value) : hints).map((hint, idx) => (
          <span key={idx} className={classNames(styles.hint, { [styles.hide]: hint.hidden })}>
            {hint.label}
          </span>
        ))}
      </div>
    </form>
  );
};
